/**
 * WorkCard component for displaying a single work in the bulk download UI.
 * Shows thumbnail, title, metadata (author, year, views), and optional relevancy badge.
 */

import React from 'react';
import styled from 'astroturf/react';
import Badge from 'js/design_system/Badge';
import { Body } from 'js/design_system/Body';
import $image_url from 'js/utils/image_url';

import { BundleWork } from './types';
import _WorkCardContainer from "WorkCard-WorkCardContainer.module.scss!=!astroturf/inline-loader?style=1!/var/lib/jenkins-code/academia-app/app/assets/javascripts/bulk_download/work_cards_shared/WorkCard.tsx?styleId=WorkCardContainer";
import _WorkRow from "WorkCard-WorkRow.module.scss!=!astroturf/inline-loader?style=1!/var/lib/jenkins-code/academia-app/app/assets/javascripts/bulk_download/work_cards_shared/WorkCard.tsx?styleId=WorkRow";
import _Thumb from "WorkCard-Thumb.module.scss!=!astroturf/inline-loader?style=1!/var/lib/jenkins-code/academia-app/app/assets/javascripts/bulk_download/work_cards_shared/WorkCard.tsx?styleId=Thumb";
import _WorkMeta from "WorkCard-WorkMeta.module.scss!=!astroturf/inline-loader?style=1!/var/lib/jenkins-code/academia-app/app/assets/javascripts/bulk_download/work_cards_shared/WorkCard.tsx?styleId=WorkMeta";
import _TitleClamp from "WorkCard-TitleClamp.module.scss!=!astroturf/inline-loader?style=1!/var/lib/jenkins-code/academia-app/app/assets/javascripts/bulk_download/work_cards_shared/WorkCard.tsx?styleId=TitleClamp";
import _WorkSubRow from "WorkCard-WorkSubRow.module.scss!=!astroturf/inline-loader?style=1!/var/lib/jenkins-code/academia-app/app/assets/javascripts/bulk_download/work_cards_shared/WorkCard.tsx?styleId=WorkSubRow";
import _RelevancyBadge from "WorkCard-RelevancyBadge.module.scss!=!astroturf/inline-loader?style=1!/var/lib/jenkins-code/academia-app/app/assets/javascripts/bulk_download/work_cards_shared/WorkCard.tsx?styleId=RelevancyBadge";
import _CompactBadgeWrapper from "WorkCard-CompactBadgeWrapper.module.scss!=!astroturf/inline-loader?style=1!/var/lib/jenkins-code/academia-app/app/assets/javascripts/bulk_download/work_cards_shared/WorkCard.tsx?styleId=CompactBadgeWrapper";
import _AnnotationRow from "WorkCard-AnnotationRow.module.scss!=!astroturf/inline-loader?style=1!/var/lib/jenkins-code/academia-app/app/assets/javascripts/bulk_download/work_cards_shared/WorkCard.tsx?styleId=AnnotationRow";
import _AnnotationTag from "WorkCard-AnnotationTag.module.scss!=!astroturf/inline-loader?style=1!/var/lib/jenkins-code/academia-app/app/assets/javascripts/bulk_download/work_cards_shared/WorkCard.tsx?styleId=AnnotationTag";


function formatRelevancyBadgeText(
  relevancy: number,
  label: 'match' | 'relevancy' = 'match',
): string {
  return `${relevancy}% ${label}`;
}

/**
 * Normalize ALL-CAPS author names to title case for display.
 *
 * Only transforms the string when it is **entirely** uppercase
 * (e.g. "MARIA ELVIRA RUIZ" -> "Maria Elvira Ruiz").
 * Mixed-case names like "McDonald", "de la Cruz", or "van der Waals"
 * are returned unchanged to avoid mangling them.
 *
 * Uses locale-aware case conversion so accented characters are handled
 * correctly (e.g. "JOSÉ MARÍA" -> "José María").
 */
function normalizeAllCapsName(name: string): string {
  // Only transform when the entire string is uppercase; leave
  // properly-cased names untouched.
  if (name !== name.toLocaleUpperCase()) return name;

  return name
    .toLocaleLowerCase()
    .split(' ')
    .map((word) => word.charAt(0).toLocaleUpperCase() + word.slice(1))
    .join(' ');
}

/** Build metadata parts for work card: authors, year, views */
function getWorkMetaParts(work: BundleWork): string[] {
  const parts: string[] = [];
  if (work.authors) {
    parts.push(`by ${normalizeAllCapsName(work.authors)}`);
  }
  if (work.year) {
    parts.push(String(work.year));
  }
  if (work.viewCount) {
    parts.push(`${work.viewCount} ${work.viewCount === 1 ? 'view' : 'views'}`);
  }
  return parts;
}

/** Render work metadata with separators and proper spacing */
function WorkMetaLine({ work }: { work: BundleWork }) {
  const parts = getWorkMetaParts(work);
  if (parts.length === 0) return null;

  return (
    <WorkSubRow>
      {parts.map((part, i) => (
        <React.Fragment key={i}>
          {i > 0 && (
            <Body size="xs" as="span" style={secondaryText}>
              |
            </Body>
          )}
          <Body size="xs" as="span" style={secondaryText}>
            {part}
          </Body>
        </React.Fragment>
      ))}
    </WorkSubRow>
  );
}

export interface WorkCardProps {
  work: BundleWork;
  /** Optional header text displayed above the work (e.g., "The paper you are reading") */
  header?: string;
  /** Whether to show the relevancy badge */
  showRelevancy?: boolean;
  /** Badge wording: "match" (default) or "relevancy" (e.g. for SWP PDF Pack widget per Figma). */
  relevancyLabel?: 'match' | 'relevancy';
  /** Whether to show admin-only debug annotations (bucket, similarity score). */
  showAnnotations?: boolean;
  /** Compact padding: reduced vertical padding, zero horizontal (parent provides it). */
  compact?: boolean;
  /** Where to render the relevancy badge. Default is 'side' (right of title).
   *  'above' places it above the title inside the meta column. */
  badgePlacement?: 'side' | 'below' | 'above';
}

export function WorkCard({
  work,
  header,
  showRelevancy = true,
  relevancyLabel = 'match',
  showAnnotations = false,
  compact = false,
  badgePlacement = 'side',
}: WorkCardProps) {
  const thumbnailSrc = work.thumbnailUrl
    ? $image_url(work.thumbnailUrl)
    : '/images/blank-paper.jpg';

  const hasAnnotationData = work.bucket || work.similarityScore != null;
  const shouldShowAnnotations = hasAnnotationData && showAnnotations;

  const badge =
    showRelevancy &&
    work.relevancy != null &&
    (compact ? (
      <CompactBadgeWrapper>
        <Badge
          text={formatRelevancyBadgeText(work.relevancy, relevancyLabel)}
          backgroundColor="var(--brand-colors-expanded-colors-lime-100, #f2f5ef)"
          color="var(--brand-colors-expanded-colors-lime-900, #333e2c)"
          height="22px"
          style={{
            border:
              '1px solid var(--brand-colors-expanded-colors-lime-200, #dee6d6)',
            fontSize: '11px',
            borderRadius: '6px',
            padding: '0 6px',
          }}
        />
      </CompactBadgeWrapper>
    ) : (
      <RelevancyBadge
        text={formatRelevancyBadgeText(work.relevancy, relevancyLabel)}
        backgroundColor="var(--brand-colors-expanded-colors-lime-100, #f2f5ef)"
        color="var(--brand-colors-expanded-colors-lime-900, #333e2c)"
        style={{
          border:
            '1px solid var(--brand-colors-expanded-colors-lime-200, #dee6d6)',
        }}
      />
    ));

  return (
    <WorkCardContainer data-compact={compact || undefined}>
      {header && (
        <Body size="xs" as="div" style={secondaryText}>
          {header}
        </Body>
      )}
      <WorkRow data-compact={compact || undefined}>
        <Thumb>
          <img alt="" src={thumbnailSrc} />
        </Thumb>
        <WorkMeta>
          {badgePlacement === 'above' && badge}
          <TitleClamp>
            <Body size="sm" bold as="span">
              {work.title}
            </Body>
          </TitleClamp>
          <WorkMetaLine work={work} />
          {badgePlacement === 'below' && badge}
        </WorkMeta>
        {badgePlacement === 'side' && badge}
      </WorkRow>
      {shouldShowAnnotations && (
        <AnnotationRow>
          {work.bucket && (
            <AnnotationTag>
              {work.bucket}
              {work.bucketIndex != null && `[${work.bucketIndex}]`}
            </AnnotationTag>
          )}
          {work.similarityScore != null && (
            <AnnotationTag>
              sim: {work.similarityScore.toFixed(3)}
            </AnnotationTag>
          )}
        </AnnotationRow>
      )}
    </WorkCardContainer>
  );
}

const secondaryText: React.CSSProperties = {
  color: 'var(--text-secondary, #535366)',
};

// Styled components

const WorkCardContainer = /*#__PURE__*/styled("div", null, {
  displayName: "WorkCardContainer",
  styles: _WorkCardContainer
});

const WorkRow = /*#__PURE__*/styled("div", null, {
  displayName: "WorkRow",
  styles: _WorkRow
});

const Thumb = /*#__PURE__*/styled("div", null, {
  displayName: "Thumb",
  styles: _Thumb
});

const WorkMeta = /*#__PURE__*/styled("div", null, {
  displayName: "WorkMeta",
  styles: _WorkMeta
});

/** Clamps title text to 2 lines. Typography is handled by the Body inside. */
const TitleClamp = /*#__PURE__*/styled("div", null, {
  displayName: "TitleClamp",
  styles: _TitleClamp
});

const WorkSubRow = /*#__PURE__*/styled("div", null, {
  displayName: "WorkSubRow",
  styles: _WorkSubRow
});

const RelevancyBadge = /*#__PURE__*/styled(Badge, null, {
  displayName: "RelevancyBadge",
  styles: _RelevancyBadge
});

const CompactBadgeWrapper = /*#__PURE__*/styled("span", null, {
  displayName: "CompactBadgeWrapper",
  styles: _CompactBadgeWrapper
});

const AnnotationRow = /*#__PURE__*/styled("div", null, {
  displayName: "AnnotationRow",
  styles: _AnnotationRow
});

/** Debug annotation pill. Uses monospace directly (not DS Mono which
 *  applies text-transform: uppercase, mangling camelCase bucket names). */
const AnnotationTag = /*#__PURE__*/styled("span", null, {
  displayName: "AnnotationTag",
  styles: _AnnotationTag
});

export default WorkCard;
